import React, { useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import { CaretRightOutlined } from '@ant-design/icons';
import styles from './DashboardSummary.css';

const EventCollapse = ({ events = [], cluster = {}, renderEventContent, onEventToggle = null }) => {
  const [expandedEvents, setExpandedEvents] = useState({});
  const contentRefs = useRef({});

  const toggleEvent = (eventKey) => {
    try {
      setExpandedEvents(prev => ({
        ...prev,
        [eventKey]: !prev[eventKey]
      }));

      // Trigger parent recalculation after a short delay to allow state update
      if (onEventToggle && typeof onEventToggle === 'function') {
        setTimeout(() => {
          try {
            onEventToggle();
          } catch (error) {
            console.error('Error in onEventToggle callback:', error);
          }
        }, 10);
      }
    } catch (error) {
      console.error('Error toggling event:', error);
    }
  };

  // Force parent containers to recalculate height when events are expanded
  useEffect(() => {
    // Small delay to allow content to render before triggering reflow
    const timer = setTimeout(() => {
      // Trigger a reflow by temporarily modifying a style
      const parentElement = document.querySelector(`.${styles.summaryContent}`);
      if (parentElement) {
        const originalOverflow = parentElement.style.overflow;
        parentElement.style.overflow = 'hidden';
        // Force a reflow
        parentElement.offsetHeight;
        parentElement.style.overflow = originalOverflow;
      }
    }, 10);

    return () => clearTimeout(timer);
  }, [expandedEvents]);

  if (!events || events.length === 0) {
    return null;
  }

  return (
    <div className={styles.customEventsCollapse}>
      {events.map((event, index) => {
        const eventKey = `${cluster.cluster_id || 'unknown'}-${index}`;
        const isExpanded = expandedEvents[eventKey];

        // Helper to get relevance class for events
        const getEventRelevanceClass = (score) => {
          // Handle missing, NA, or invalid scores
          if (score === undefined || score === null || score === 'NA' || score === 'N/A' ||
              (typeof score === 'string' && isNaN(parseFloat(score)))) {
            return ''; // No class if no valid score
          }

          // Convert to number if it's a string
          const numScore = typeof score === 'string' ? parseFloat(score) : score;

          if (isNaN(numScore)) {return '';} // Safety check

          if (numScore >= 0.7) {return styles.highRelevance;}
          if (numScore >= 0.4) {return styles.mediumRelevance;}
          return styles.lowRelevance;
        };

        const eventRelevanceClass = getEventRelevanceClass(event.relevance_score);

        return (
          <div
            key={eventKey}
            className={classnames(styles.customEventItem, {
              [styles.customEventItemExpanded]: isExpanded
            }, eventRelevanceClass)}
            style={{
              // Ensure the item can grow to accommodate content
              minHeight: 'auto',
              height: 'auto'
            }}
          >
            <div
              className={styles.customEventHeader}
              onClick={() => toggleEvent(eventKey)}
              role="button"
              tabIndex={0}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  toggleEvent(eventKey);
                }
              }}
              aria-expanded={isExpanded}
            >
              <CaretRightOutlined
                className={classnames(styles.customEventIcon, {
                  [styles.customEventIconExpanded]: isExpanded
                })}
              />
              <span className={styles.customEventTitle}>{event.title}</span>
            </div>

            {isExpanded && (
              <div
                ref={el => contentRefs.current[eventKey] = el}
                className={styles.customEventContent}
                style={{
                  // Ensure content can flow naturally
                  maxHeight: 'none',
                  height: 'auto',
                  overflow: 'visible'
                }}
              >
                {renderEventContent(event)}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};

EventCollapse.propTypes = {
  events: PropTypes.array,
  cluster: PropTypes.object.isRequired,
  renderEventContent: PropTypes.func.isRequired,
  onEventToggle: PropTypes.func
};

export default EventCollapse;
